import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, AppBar, Toolbar, Typography, Paper, Grid, CircularProgress,
  Chip, Divider, Tooltip, IconButton, Button, TextField, Select, MenuItem,
  InputAdornment, FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Stack, LinearProgress, Checkbox, FormControlLabel,
  Tabs, Tab,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import LoadingBar from 'react-top-loading-bar';
import {
  MenuBook as SyllabusIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ContentCopy as CloneIcon, UploadFile as ImportIcon, Download as DownloadIcon,
  Close as CloseIcon, Search as SearchIcon, Refresh as RefreshIcon,
  NavigateBefore as PrevIcon, NavigateNext as NextIcon, CheckCircle as CheckIcon,
  Warning as WarnIcon, PlaylistAddCheck as TrackIcon, CloudUpload as CloudUploadIcon,
  SwapHorizontalCircle as SwapIcon, BarChart as BarChartIcon, PieChart as PieChartIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';

/* ============================================================================
   Teacher Panel — Manage Syllabus
   Functional parity with the admin Manage Syllabus module:
   - ONE shared set of filters (Class / Section / Subject / Month / Financial
     Year) drives all three tabs, exactly like the admin panel. Changing any
     filter re-fires the List, Analytics AND Report loads together.
   - Subject dropdowns are class-scoped (ClassMapping/GetSubjectMapping),
     filtered to status === '1', same as admin's filtefSubjects/formSubjects/
     cloneSubjects.
   - Create = POST, Update = PUT, Delete = DELETE (matches admin exactly).
   - Analytics "coverage by class" is computed across the full class master
     list, same as admin.
   Visual style only is skinned to the plain "Topic Progress" look: orange
   AppBar, bordered tables, plain chips, react-top-loading-bar.
   ============================================================================ */

const drawerWidth = 240;

const COLORS = {
  primary: '#063552',
  primaryDark: '#063552',
  success: '#2e7d32',
  successBg: '#e8f5e9',
  warn: '#063552',
  info: '#094669',
  infoBg: '#e1f5fe',
  muted: '#616161',
  border: '#ccc',
  bg: '#f4f6f9',
};

const ROW_OPTIONS = [5, 10, 15, 25];
const STATUS_MAP = { P: 'Pending', C: 'Covered', IP: 'In Progress' };

// ---- API base / helpers (identical to admin panel) --------------------
const BASE = 'https://wsmsv1.aidigitalsec.com/api';
const tok = () => sessionStorage.getItem('token');
const authH = () => ({ Authorization: `${tok()}` });
const postH = () => ({ 'Content-Type': 'application/json', ...authH() });
const safeJ = async (r) => { try { return await r.json(); } catch { return null; } };

const apiPost = (path, body) => fetch(`${BASE}${path}`, {
  method: 'POST', headers: postH(), body: JSON.stringify(body ?? {}),
});
const apiPostForm = (path, formData) => fetch(`${BASE}${path}`, {
  method: 'POST', headers: authH(), body: formData,
});
const apiGet = (path) => fetch(`${BASE}${path}`, { method: 'GET', headers: authH() });
const apiPut = (path, body) => fetch(`${BASE}${path}`, {
  method: 'PUT', headers: postH(), body: JSON.stringify(body ?? {}),
});
const apiDelete = (path) => fetch(`${BASE}${path}`, { method: 'DELETE', headers: authH() });

// Master Data Fetching — same endpoints as admin panel, no teacher scoping.
const fetchClasses = () => apiPost('/Enquiry/ddlclassName', {}).then(safeJ);
const fetchSubjects = () => apiPost('/SubjectMaster/GetSubject', {}).then(safeJ);
const fetchFinancialYears = () => apiPost('/ClassPromotion/GetFinancialYear', {}).then(safeJ);
const fetchMonths = () => apiPost('/Fine/GetMonthList', {}).then(safeJ);
const fetchSectionsByClass = (classId) => classId
  ? apiPost('/ClassPromotion/ddlSection_clsId', { teacherId: 0, classId }).then(safeJ)
  : Promise.resolve([]);
// Class-scoped subjects — same endpoint the admin panel uses for every
// subject dropdown (filter row, create/edit form, clone form).
const fetchSubjectsByClass = (classId) => classId
  ? apiPost('/ClassMapping/GetSubjectMapping', { classId }).then(safeJ)
  : Promise.resolve([]);
const fetchUserGroups = () => apiPost('/CPanel/GetUserGroup', {}).then(safeJ);

// field accessors (match admin panel field names)
const cId = (c) => c.classId; const cName = (c) => c.className;
const secId = (s) => s.sectionId; const secName = (s) => s.sectionName;
const subId = (s) => s.subjectId; const subName = (s) => s.subjectName;
const moId = (m) => m.monthId; const moName = (m) => m.month;
const fyId = (f) => f.financialYearID; const fyName = (f) => f.finanacialYear;

const currentFYLabel = () => {
  const now = new Date();
  const y = now.getFullYear();
  return now.getMonth() >= 3 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const uid = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const coverageState = (pct) => {
  if (pct >= 100) return { label: 'Completed', color: COLORS.success };
  if (pct > 0) return { label: 'In Progress', color: COLORS.info };
  return { label: 'Not Covered', color: COLORS.muted };
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function useDisableStickyMenuHover() {
  useEffect(() => {
    if (document.getElementById('no-sticky-menu-hover')) return;
    const style = document.createElement('style');
    style.id = 'no-sticky-menu-hover';
    style.innerHTML = `
      @media (hover: none) {
        .MuiMenuItem-root:hover,
        .MuiMenuItem-root.Mui-focusVisible {
          background-color: transparent !important;
        }
        .MuiMenuItem-root.Mui-selected {
          background-color: linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%) !important;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);
}

/* ---------- small reusable bits (plain style, matches Topic Progress) ---------- */

const TH = ({ children, align = 'left', width }) => (
  <TableCell align={align} sx={{
    fontWeight: 'bold', borderRight: `1px solid ${COLORS.border}`, bgcolor: '#fafafa',
    ...(width ? { width } : {}),
  }}>{children}</TableCell>
);

const StatusChip = ({ status }) => {
  const map = {
    Completed: 'success', Covered: 'success',
    'In Progress': 'info', 'Not Covered': 'default', Pending: 'error',
  };
  return <Chip label={status || '—'} color={map[status] || 'default'} size="small" sx={{ fontWeight: 'bold' }} />;
};

const CoverageBar = ({ pct = 0, total, covered }) => {
  const p = Math.max(0, Math.min(100, pct));
  const st = coverageState(p);
  return (
    <Tooltip title={total !== undefined ? `${covered} of ${total} topics covered` : ''}>
      <Box sx={{ minWidth: 90 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: st.color }}>{Math.round(p)}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate" value={p}
          sx={{ height: 8, borderRadius: 4, bgcolor: '#eee', '& .MuiLinearProgress-bar': { bgcolor: st.color, borderRadius: 4 } }}
        />
      </Box>
    </Tooltip>
  );
};

const SearchBar = ({ value, onChange, placeholder = 'Search…' }) => (
  <TextField
    size="small" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} fullWidth
    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
  />
);

const Pager = ({ total, page, rowsPerPage, onPage, onRows }) => {
  const pages = Math.max(Math.ceil(total / rowsPerPage), 1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 1.2, borderTop: `1px solid ${COLORS.border}`, flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption">Rows per page:</Typography>
        <Select value={rowsPerPage} onChange={e => { onRows(Number(e.target.value)); onPage(1); }} size="small" sx={{ height: 30, minWidth: 60 }}>
          {ROW_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption">
          {total === 0 ? '0' : `${Math.min((page - 1) * rowsPerPage + 1, total)}–${Math.min(page * rowsPerPage, total)}`} of {total}
        </Typography>
        <IconButton size="small" onClick={() => onPage(p => p - 1)} disabled={page <= 1}><PrevIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={() => onPage(p => p + 1)} disabled={page >= pages}><NextIcon fontSize="small" /></IconButton>
      </Box>
    </Box>
  );
};

const KpiBox = ({ label, value, suffix = '' }) => (
  <Paper variant="outlined" sx={{ px: 2, py: 1, textAlign: 'center', minWidth: 100 }}>
    <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem', color: COLORS.primaryDark }}>{Math.round(value || 0)}{suffix}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Paper>
);

/* ---------- one row of Class/Section/Subject/Month/FY selects, bound to the
   single shared filter state (identical values across all three tabs, same
   as the admin panel) ---------- */
const FilterRow = ({
  classId, setClassId, sectionId, setSectionId, subjectId, setSubjectId,
  monthId, setMonthId, financialYearId, setFinancialYearId,
  classes, sectionOptions, subjectOptions, months, fys,
  onReload, reloading, reloadLabel = 'Reload', extraButtons,
}) => (
  <Grid container spacing={1.2} alignItems="center">
    <Grid item xs={12} sm={6} md={2}>
      <FormControl fullWidth size="small">
        <InputLabel>Class</InputLabel>
        <Select label="Class" value={classId} onChange={e => setClassId(e.target.value)}>
          <MenuItem value=""><em>All Classes</em></MenuItem>
          {classes.map(c => <MenuItem key={cId(c)} value={cId(c)}>{cName(c)}</MenuItem>)}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} sm={6} md={2}>
      <FormControl fullWidth size="small" disabled={!classId}>
        <InputLabel>Section</InputLabel>
        <Select label="Section" value={sectionId} onChange={e => setSectionId(e.target.value)}>
          <MenuItem value=""><em>All Sections</em></MenuItem>
          {sectionOptions.map(s => <MenuItem key={secId(s)} value={secId(s)}>{secName(s)}</MenuItem>)}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} sm={6} md={2}>
      <FormControl fullWidth size="small" disabled={!classId}>
        <InputLabel>Subject</InputLabel>
        <Select label="Subject" value={subjectId} onChange={e => setSubjectId(e.target.value)}>
          <MenuItem value=""><em>All Subjects</em></MenuItem>
          {subjectOptions.map(s => <MenuItem key={subId(s)} value={subId(s)}>{subName(s)}</MenuItem>)}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} sm={6} md={2}>
      <FormControl fullWidth size="small">
        <InputLabel>Month</InputLabel>
        <Select label="Month" value={monthId} onChange={e => setMonthId(e.target.value)}>
          <MenuItem value=""><em>All Months</em></MenuItem>
          {months.map(m => <MenuItem key={moId(m)} value={moId(m)}>{moName(m)}</MenuItem>)}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} sm={6} md={2}>
      <FormControl fullWidth size="small">
        <InputLabel>Financial Year</InputLabel>
        <Select label="Financial Year" value={financialYearId} onChange={e => setFinancialYearId(e.target.value)}>
          {fys.map(y => <MenuItem key={fyId(y)} value={fyId(y)}>{fyName(y)}</MenuItem>)}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} md={2}>
      <Button variant="outlined" sx={{
      color: "#063552",
      borderColor: "#063552",
      "&:hover": {
      borderColor: "#102A43",
      backgroundColor: "rgba(27, 73, 101, 0.08)",
    },
    }} fullWidth startIcon={<RefreshIcon fontSize="small" />} onClick={onReload} disabled={reloading}>
        {reloadLabel}
      </Button>
    </Grid>
    {extraButtons && (
      <Grid item xs={12} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>{extraButtons}</Box>
      </Grid>
    )}
  </Grid>
);

/* ---------- Analytics charts ---------- */
const CoverageByClassChart = ({ data }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <BarChartIcon sx={{ color: COLORS.primary }} fontSize="small" />
      <Typography sx={{ fontWeight: 'bold' }}>Coverage by Class</Typography>
    </Box>
    {data.length === 0 ? (
      <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
        No coverage data yet for the current filters.
      </Box>
    ) : (
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="className" fontSize={12} />
          <YAxis fontSize={12} />
          <RechartsTooltip formatter={(v) => [`${v.toFixed ? v.toFixed(1) : v}%`, 'Coverage']} />
          <Bar dataKey="coverage" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </Paper>
);

const StatusPieChart = ({ data }) => {
  const PIE_COLORS = [COLORS.success, COLORS.info, COLORS.warn];
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <PieChartIcon sx={{ color: COLORS.primary }} fontSize="small" />
        <Typography sx={{ fontWeight: 'bold' }}>Topic Status Distribution</Typography>
      </Box>
      {data.length === 0 ? (
        <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
          No topics tracked yet for the current filters.
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <Pie data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3} dataKey="value"
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
              {data.map((entry, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#fff" strokeWidth={2} />)}
            </Pie>
            <RechartsTooltip />
            <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};

const OverallCompletionCard = ({ pct, classCount }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, justifyContent: 'center', alignItems: 'center' }}>
    <Typography sx={{ fontWeight: 'bold', alignSelf: 'flex-start' }}>Overall Completion</Typography>
    <Typography sx={{ fontSize: '2.4rem', fontWeight: 'bold', color: COLORS.primaryDark }}>{Math.round(pct)}%</Typography>
    <Box sx={{ width: '80%' }}>
      <LinearProgress variant="determinate" value={Math.min(100, pct)} sx={{ height: 10, borderRadius: 5, bgcolor: '#eee', '& .MuiLinearProgress-bar': { bgcolor: COLORS.primary, borderRadius: 5 } }} />
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
      Averaged across {classCount || 0} class{classCount === 1 ? '' : 'es'} with active syllabus
    </Typography>
  </Paper>
);

/* ============================================================================
   MAIN COMPONENT
   ============================================================================ */
export default function TeacherManageSyllabus() {
  useDisableStickyMenuHover();

  // Branch (single branch — no branch switcher, same as a non-master admin user)
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [cloneConfirmOpen, setCloneConfirmOpen] = useState(false);

  // Global master lists
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]); // full list, fetched for parity (not rendered directly, matches admin)
  const [months, setMonths] = useState([]);
  const [fys, setFys] = useState([]);
  const [mastersLoaded, setMastersLoaded] = useState(false);

  const [loadingBarProgress, setLoadingBarProgress] = useState(0);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const showSnack = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const [activeTab, setActiveTab] = useState(0);

  // ---------------------------------------------------------------------
  // SHARED FILTERS — one set of values drives all three tabs, exactly like
  // the admin panel (fClass / fSection / fSubject / fMonth / fFy).
  // ---------------------------------------------------------------------
  const [fClass, setFClass] = useState('');
  const [fSection, setFSection] = useState('');
  const [fSubject, setFSubject] = useState('');
  const [fMonth, setFMonth] = useState('');
  const [fFy, setFFy] = useState('');
  const [filtefSections, setFiltefSections] = useState([]);
  const [filtefSubjects, setFiltefSubjects] = useState([]);

  // ----- Tab 0: Syllabus List (own paging/search only) -----
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // ----- Tab 1: Analytics -----
  const [analyticsList, setAnalyticsList] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({ coverageByClass: [], statusDistribution: [] });

  // ----- Tab 2: Coverage Report -----
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ----- Add/Edit dialog -----
  const emptyForm = {
    SyllabusId: null, ClassId: '', SectionId: '', SubjectId: '', FinancialYearId: '', MonthId: '',
    ThemeNo: 1, themeName: '', themeDescription: '',
    Topics: [{ _id: uid(), TopicSequence: 1, TopicName: '' }],
  };
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formLoadingDetail, setFormLoadingDetail] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formSections, setFormSections] = useState([]);
  const [formSubjects, setFormSubjects] = useState([]);

  useEffect(() => {
    if (formMode === 'create') fetchSectionsByClass(form.ClassId).then(d => setFormSections(Array.isArray(d) ? d : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.ClassId, formMode]);

  useEffect(() => {
    if (formMode === 'create') fetchSubjectsByClass(form.ClassId).then(d => {
      setFormSubjects(Array.isArray(d) ? d.filter(s => s.status === '1') : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.ClassId, formMode]);

  // ----- Delete dialog -----
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRec, setDeleteRec] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ----- Track topic coverage dialog -----
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackSyllabus, setTrackSyllabus] = useState(null);
  const [trackTopics, setTrackTopics] = useState([]);
  const [trackSaving, setTrackSaving] = useState(false);
  const [expandedRemarks, setExpandedRemarks] = useState({});

  // ----- Clone dialog -----
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSaving, setCloneSaving] = useState(false);
  const [cloneClasses, setCloneClasses] = useState([]);
  const [cloneSections, setCloneSections] = useState([]);
  const [cloneSubjects, setCloneSubjects] = useState([]);
  const [cloneForm, setCloneForm] = useState({
    ClassId: '', SectionId: '', SubjectId: '', MonthId: '',
    FromFinancialYearId: '', ToFinancialYearId: '', CloneTopics: true, ResetTracking: true,
  });

  useEffect(() => {
    fetchSectionsByClass(cloneForm.ClassId).then(d => setCloneSections(Array.isArray(d) ? d : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneForm.ClassId]);

  useEffect(() => {
    fetchSubjectsByClass(cloneForm.ClassId).then(d => {
      setCloneSubjects(Array.isArray(d) ? d.filter(s => s.status === '1') : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneForm.ClassId]);

  // ----- Import dialog -----
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  /* ---------------- init ---------------- */
  useEffect(() => {
    const branchId = sessionStorage.getItem('branchId');
    if (branchId) setSelectedBranchId(Number(branchId));
    loadMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMasters = async () => {
    setLoadingBarProgress(30);
    try {
      const [c, sub, fy, mo] = await Promise.all([fetchClasses(), fetchSubjects(), fetchFinancialYears(), fetchMonths()]);
      const classArr = Array.isArray(c) ? c : [];
      const subArr = Array.isArray(sub) ? sub : [];
      const fyArr = Array.isArray(fy) ? fy : [];
      const moArr = Array.isArray(mo) ? mo : [];
      setClasses(classArr); setSubjects(subArr); setFys(fyArr); setMonths(moArr);

      const label = currentFYLabel();
      const match = fyArr.find(f => fyName(f) === label);
      const defaultFyId = (match ? fyId(match) : fyArr[fyArr.length - 1] && fyId(fyArr[fyArr.length - 1])) || '';
      setFFy(defaultFyId);

      const currentMonthName = new Date().toLocaleString('en-US', { month: 'long' });
      const monthMatch = moArr.find(m => moName(m)?.toLowerCase() === currentMonthName.toLowerCase());
      if (monthMatch) setFMonth(moId(monthMatch));

      setLoadingBarProgress(100);
    } catch (e) {
      console.error('Failed to load master data', e);
      showSnack('Failed to load dropdown data', 'error');
      setLoadingBarProgress(0);
    } finally {
      setMastersLoaded(true);
    }
  };

  // Cascading sections/subjects for the shared filter row — resets the
  // dependent value whenever the class changes, same as admin.
  useEffect(() => {
    fetchSectionsByClass(fClass).then(d => {
      setFiltefSections(Array.isArray(d) ? d : []);
      setFSection('');
    });
  }, [fClass]);

  useEffect(() => {
    fetchSubjectsByClass(fClass).then(d => {
      setFiltefSubjects(Array.isArray(d) ? d : []);
      setFSubject('');
    });
  }, [fClass]);

  /* ---------------- Syllabus List ---------------- */
  const loadList = useCallback(async () => {
    if (!selectedBranchId) return;
    setListLoading(true); setLoadingBarProgress(30);
    try {
      const body = {
        ClassId: fClass || 0, SectionId: fSection || 0, SubjectId: fSubject || 0,
        MonthId: fMonth || 0, FinancialYearId: fFy || 0,
      };
      const res = await apiPost(`/CircularSyllabus/GetByClassSubject?Branch_id=${selectedBranchId}`, body);
      const d = await safeJ(res);
      setList(Array.isArray(d?.syllabusList) ? d.syllabusList : []);
      setLoadingBarProgress(100);
    } catch (e) {
      console.error(e);
      showSnack('Failed to load syllabus list', 'error');
      setLoadingBarProgress(0);
    } finally { setListLoading(false); }
  }, [selectedBranchId, fClass, fSection, fSubject, fMonth, fFy]);

  /* ---------------- Analytics ---------------- */
  const updateAnalyticsData = (syllabusList) => {
    // Full class master list, same as admin — not just classes present in
    // the fetched syllabus list.
    const classCoverage = classes.map(c => {
      const syllabusForClass = syllabusList.filter(s => s.classId === cId(c));
      const totalTopics = syllabusForClass.reduce((sum, s) => sum + (s.totalTopics || 0), 0);
      const coveredTopics = syllabusForClass.reduce((sum, s) => sum + (s.coveredTopics || 0), 0);
      const coverage = totalTopics ? (coveredTopics / totalTopics) * 100 : 0;
      return { className: cName(c), coverage };
    }).filter(c => c.coverage > 0);

    const totalPending = syllabusList.reduce((sum, s) => sum + (s.pendingTopics || 0), 0);
    const totalCovered = syllabusList.reduce((sum, s) => sum + (s.coveredTopics || 0), 0);
    const totalInProgress = syllabusList.reduce((sum, s) => sum + (s.totalTopics || 0) - (s.coveredTopics || 0) - (s.pendingTopics || 0), 0);

    const statusData = [
      { name: 'Covered', value: totalCovered },
      { name: 'In Progress', value: Math.max(0, totalInProgress) },
      { name: 'Pending', value: totalPending },
    ].filter(s => s.value > 0);

    setAnalyticsData({ coverageByClass: classCoverage, statusDistribution: statusData });
  };

  const loadAnalytics = useCallback(async () => {
    if (!selectedBranchId) return;
    setAnalyticsLoading(true); setLoadingBarProgress(30);
    try {
      const body = {
        ClassId: fClass || 0, SectionId: fSection || 0, SubjectId: fSubject || 0,
        MonthId: fMonth || 0, FinancialYearId: fFy || 0,
      };
      const res = await apiPost(`/CircularSyllabus/GetByClassSubject?Branch_id=${selectedBranchId}`, body);
      const d = await safeJ(res);
      const syllabusList = Array.isArray(d?.syllabusList) ? d.syllabusList : [];
      setAnalyticsList(syllabusList);
      updateAnalyticsData(syllabusList);
      setLoadingBarProgress(100);
    } catch (e) {
      console.error(e);
      showSnack('Failed to load analytics', 'error');
      setLoadingBarProgress(0);
    } finally { setAnalyticsLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, fClass, fSection, fSubject, fMonth, fFy, classes]);

  const analyticsKpis = useMemo(() => {
    const totalTopics = analyticsList.reduce((s, r) => s + (r.totalTopics ?? 0), 0);
    const coveredTopics = analyticsList.reduce((s, r) => s + (r.coveredTopics ?? 0), 0);
    return { avgCoverage: totalTopics ? (coveredTopics / totalTopics) * 100 : 0 };
  }, [analyticsList]);

  /* ---------------- Coverage Report ---------------- */
  const reportFilterBody = () => ({
    ClassId: fClass || 0, SectionId: fSection || 0, SubjectId: fSubject || 0,
    MonthId: fMonth || 0, FinancialYearId: fFy || 0, FromDate: null, ToDate: null,
  });

  const openReport = useCallback(async () => {
    if (!selectedBranchId) return;
    setReportLoading(true); setLoadingBarProgress(30);
    try {
      const res = await apiPost(`/CircularSyllabus/GetReport?Branch_id=${selectedBranchId}`, reportFilterBody());
      const d = await safeJ(res);
      setReportRows(Array.isArray(d?.report) ? d.report : Array.isArray(d) ? d : []);
      setLoadingBarProgress(100);
    } catch (e) {
      console.error(e);
      showSnack('Failed to load report', 'error');
      setLoadingBarProgress(0);
    } finally { setReportLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, fClass, fSection, fSubject, fMonth, fFy]);

  // Any filter change reloads ALL THREE tabs together — matches admin.
  useEffect(() => {
    if (selectedBranchId && mastersLoaded) loadList();
  }, [selectedBranchId, fClass, fSection, fSubject, fMonth, fFy, mastersLoaded, loadList]);

  useEffect(() => {
    if (selectedBranchId && mastersLoaded) loadAnalytics();
  }, [selectedBranchId, mastersLoaded, fClass, fSection, fSubject, fMonth, fFy, loadAnalytics]);

  useEffect(() => {
    if (selectedBranchId && mastersLoaded) openReport();
  }, [selectedBranchId, mastersLoaded, fClass, fSection, fSubject, fMonth, fFy, openReport]);

  const filteredList = useMemo(() => list.filter(r =>
    !search ||
    (r.themeName ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (r.subjectName ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (r.className ?? '').toLowerCase().includes(search.toLowerCase())
  ), [list, search]);

  const pagedList = useMemo(() => filteredList.slice((page - 1) * rows, page * rows), [filteredList, page, rows]);

  const kpis = useMemo(() => {
    const totalTopics = list.reduce((s, r) => s + (r.totalTopics ?? 0), 0);
    const coveredTopics = list.reduce((s, r) => s + (r.coveredTopics ?? 0), 0);
    const avgCoverage = totalTopics ? (coveredTopics / totalTopics) * 100 : 0;
    const completed = list.filter(r => (r.totalTopics ?? 0) > 0 && (r.coveredTopics ?? 0) === r.totalTopics).length;
    return { totalSyllabus: list.length, avgCoverage, completed, pendingTopics: totalTopics - coveredTopics };
  }, [list]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiPost(`/CircularSyllabus/ExportReport?Branch_id=${selectedBranchId}`, reportFilterBody());
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'CircularSyllabus_Report.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
      showSnack('Report exported successfully');
    } catch (e) {
      console.error(e);
      showSnack('Failed to export report', 'error');
    } finally { setExporting(false); }
  };

  /* ---------------- Add / Edit form ---------------- */
  const openCreate = () => {
    setForm({
      ...emptyForm,
      ClassId: fClass || '', SectionId: fSection || '', SubjectId: fSubject || '',
      FinancialYearId: fFy || '', MonthId: fMonth || '',
      Topics: [{ _id: uid(), TopicSequence: 1, TopicName: '' }],
    });
    setFormMode('create');
    setFormOpen(true);
  };

  const openEdit = async (rec) => {
    setFormMode('edit'); setFormOpen(true); setFormLoadingDetail(true);
    try {
      const res = await apiGet(`/CircularSyllabus/GetById/${rec.syllabusId}?Branch_id=${selectedBranchId}`);
      const d = await safeJ(res);
      setForm({
        SyllabusId: d?.syllabusId ?? rec.syllabusId,
        ClassId: d?.classId ?? rec.classId,
        SectionId: d?.sectionId ?? rec.sectionId ?? '',
        SubjectId: d?.subjectId ?? rec.subjectId,
        FinancialYearId: d?.financialYearId ?? rec.financialYearId,
        MonthId: d?.monthId ?? rec.monthId,
        ThemeNo: d?.themeNo ?? rec.themeNo ?? 1,
        themeName: d?.themeName ?? rec.themeName ?? '',
        themeDescription: d?.themeDescription ?? rec.themeDescription ?? '',
        Topics: (d?.topics ?? []).length
          ? d.topics.map(t => ({ _id: uid(), TrackingId: t.trackingId, TopicSequence: t.topicSequence, TopicName: t.topicName }))
          : [{ _id: uid(), TopicSequence: 1, TopicName: '' }],
        className: d?.className ?? rec.className,
        sectionName: d?.sectionName ?? rec.sectionName,
        subjectName: d?.subjectName ?? rec.subjectName,
        MonthName: d?.monthName ?? rec.monthName,
        FinancialYear: d?.financialYear ?? rec.financialYear,
      });
    } catch (e) {
      console.error(e);
      showSnack('Failed to load syllabus details', 'error');
    } finally { setFormLoadingDetail(false); }
  };

  const addTopicRow = () => setForm(f => ({ ...f, Topics: [...f.Topics, { _id: uid(), TopicSequence: f.Topics.length + 1, TopicName: '' }] }));
  const removeTopicRow = (id) => setForm(f => {
    const next = f.Topics.filter(t => t._id !== id).map((t, i) => ({ ...t, TopicSequence: i + 1 }));
    return { ...f, Topics: next.length ? next : [{ _id: uid(), TopicSequence: 1, TopicName: '' }] };
  });
  const updateTopicName = (id, name) => setForm(f => ({ ...f, Topics: f.Topics.map(t => t._id === id ? { ...t, TopicName: name } : t) }));

  const validateForm = () => {
    if (!form.ClassId || !form.SubjectId || !form.FinancialYearId || !form.MonthId) {
      showSnack('Please select class, subject, financial year and month', 'error');
      return false;
    }
    if (Number(form.ThemeNo) < 0) {
      showSnack('Theme No cannot be negative', 'error');
      return false;
    }
    if (!form.themeName.trim()) { showSnack('Theme name is required', 'error'); return false; }
    if (form.Topics.some(t => !t.TopicName.trim())) { showSnack('All topic names must be filled, or removed', 'error'); return false; }
    return true;
  };

  const handleSaveForm = async () => {
    if (!validateForm()) return;
    setFormSaving(true);
    try {
      const topics = form.Topics.map(t => ({ TopicSequence: t.TopicSequence, TopicName: t.TopicName.trim() }));
      let res;
      if (formMode === 'create') {
        const body = {
          ClassId: form.ClassId, SectionId: form.SectionId || 0, SubjectId: form.SubjectId,
          FinancialYearId: form.FinancialYearId, MonthId: form.MonthId,
          ThemeNo: Number(form.ThemeNo) || 1, themeName: form.themeName.trim(),
          themeDescription: form.themeDescription, Topics: topics,
        };
        res = await apiPost(`/CircularSyllabus/Create?Branch_id=${selectedBranchId}`, body);
      } else {
        const body = {
          SyllabusId: form.SyllabusId, ThemeNo: Number(form.ThemeNo) || 1,
          themeName: form.themeName.trim(), themeDescription: form.themeDescription, Topics: topics,
        };
        res = await apiPut(`/CircularSyllabus/Update?Branch_id=${selectedBranchId}`, body);
      }
      const d = await safeJ(res);
      if (res.ok) {
        showSnack(d?.Msg || `Syllabus ${formMode === 'create' ? 'created' : 'updated'} successfully`);
        setFormOpen(false); loadList();
      } else showSnack(d?.Msg || 'Save failed. Please try again.', 'error');
    } catch (e) {
      console.error(e);
      showSnack('Error saving syllabus', 'error');
    } finally { setFormSaving(false); }
  };

  /* ---------------- Delete ---------------- */
  const handleDelete = async () => {
    if (!deleteRec) return;
    setDeleting(true);
    try {
      const res = await apiDelete(`/CircularSyllabus/Delete/${deleteRec.syllabusId}?Branch_id=${selectedBranchId}`);
      const d = await safeJ(res);
      if (res.ok && !(d?.msg ?? d?.Msg ?? '').toLowerCase().includes('failed')) {
        showSnack(d?.msg || d?.Msg || 'Syllabus deleted successfully');
        setDeleteOpen(false); loadList();
      } else {
        showSnack(d?.msg || d?.Msg || 'Delete failed', 'error');
      }
    } catch (e) {
      console.error(e);
      showSnack('Error deleting syllabus', 'error');
    } finally { setDeleting(false); }
  };

  /* ---------------- Track topic coverage ---------------- */
  const openTrack = async (rec) => {
    setTrackSyllabus(rec); setTrackOpen(true); setTrackLoading(true);
    try {
      const res = await apiGet(`/CircularSyllabus/GetById/${rec.syllabusId}?Branch_id=${selectedBranchId}`);
      const d = await safeJ(res);
      setTrackTopics((d?.topics ?? []).map(t => ({
        TrackingId: t.trackingId, TopicSequence: t.topicSequence, TopicName: t.topicName,
        Status: t.status ?? 'P', Description: t.coveredDescription ?? '',
        CoveredByTeacherName: t.coveredByTeacherName, CoveredDate: t.coveredDate,
        CoveredDescription: t.coveredDescription ?? '', _dirty: false,
      })));
    } catch (e) {
      console.error(e);
      showSnack('Failed to load topics', 'error');
    } finally { setTrackLoading(false); }
  };

  const updateTrackTopic = (trackingId, patch) => setTrackTopics(ts => ts.map(t => t.TrackingId === trackingId ? { ...t, ...patch, _dirty: true } : t));

  const resolveTeacherName = async () => {
    let clientName = sessionStorage.getItem('clientName');
    try { const parsed = JSON.parse(clientName); if (typeof parsed === 'string') clientName = parsed; } catch { /* raw string */ }
    clientName = (clientName || '').trim();
    if (clientName) return clientName;
    const userGroupId = sessionStorage.getItem('userGroupId');
    if (!userGroupId) return 'Unknown Teacher';
    try {
      const groups = await fetchUserGroups();
      const match = Array.isArray(groups) ? groups.find(g => String(g.userGroupId) === String(userGroupId)) : null;
      return match?.userGroupName || 'Unknown Teacher';
    } catch (e) {
      console.error('Failed to resolve user group name', e);
      return 'Unknown Teacher';
    }
  };

  const handleSaveTrack = async () => {
    const dirty = trackTopics.filter(t => t._dirty);
    if (!dirty.length) { setTrackOpen(false); return; }
    setTrackSaving(true);
    try {
      const teacherId = sessionStorage.getItem('employeeId') || 0;
      const teacherName = await resolveTeacherName();
      const body = {
        Topics: dirty.map(t => ({
          trackingId: t.TrackingId, status: t.Status,
          coveredby_TeacherId: teacherId, coveredby_TeacherName: teacherName,
          description: t.Description || '',
        })),
      };
      const res = await apiPost(`/CircularSyllabus/MarkCovered?Branch_id=${selectedBranchId}`, body);
      const d = await safeJ(res);
      if (res.ok) {
        showSnack(d?.Msg || `${d?.RowsAffected ?? dirty.length} topic(s) updated`);
        setTrackOpen(false); loadList();
      } else showSnack(d?.Msg || 'Update failed', 'error');
    } catch (e) {
      console.error(e);
      showSnack('Error updating topics', 'error');
    } finally { setTrackSaving(false); }
  };

  /* ---------------- Clone ---------------- */
  const openClone = () => {
    setCloneForm({
      ClassId: '', SectionId: '', SubjectId: '', MonthId: '',
      FromFinancialYearId: fFy || '', ToFinancialYearId: '',
      CloneTopics: true, ResetTracking: true,
    });
    setCloneOpen(true);
  };

  useEffect(() => {
    if (!cloneOpen || !cloneForm.FromFinancialYearId || !selectedBranchId) return;
    (async () => {
      try {
        const res = await apiPost(`/CircularSyllabus/GetClassesWithSyllabus?Branch_id=${selectedBranchId}&financialYearId=${cloneForm.FromFinancialYearId}`, {});
        const d = await safeJ(res);
        setCloneClasses(Array.isArray(d?.Classes) ? d.Classes : []);
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneOpen, cloneForm.FromFinancialYearId, selectedBranchId]);

 const handleClone = () => {
  if (!cloneForm.ClassId || !cloneForm.SectionId || !cloneForm.SubjectId || !cloneForm.MonthId || !cloneForm.FromFinancialYearId || !cloneForm.ToFinancialYearId) {
    showSnack('Please select all fields', 'error');
    return;
  }
  if (cloneForm.FromFinancialYearId === cloneForm.ToFinancialYearId) {
    showSnack('Source and target financial year must be different', 'error');
    return;
  }
  setCloneConfirmOpen(true); // instead of calling the API here
};

const performClone = async () => {
  setCloneSaving(true);
  try {
    const body = {
      ClassId: cloneForm.ClassId, SectionId: cloneForm.SectionId || 0, SubjectId: cloneForm.SubjectId || 0,
      MonthId: cloneForm.MonthId || 0, FromFinancialYearId: cloneForm.FromFinancialYearId,
      ToFinancialYearId: cloneForm.ToFinancialYearId, CloneTopics: cloneForm.CloneTopics, ResetTracking: cloneForm.ResetTracking,
    };
    const res = await apiPost(`/CircularSyllabus/CloneToFinancialYear?Branch_id=${selectedBranchId}`, body);
    const d = await safeJ(res);
    if (res.ok && (d?.Success ?? true)) {
      showSnack(d?.Message || `Cloned ${d?.ClonedSyllabusCount ?? ''} syllabus record(s) successfully`);
      setCloneConfirmOpen(false);
      setCloneOpen(false);
      loadList();
    } else showSnack(d?.Message || 'Clone failed', 'error');
  } catch (e) {
    console.error(e);
    showSnack('Error cloning syllabus', 'error');
  } finally { setCloneSaving(false); }
}; 

  /* ---------------- Import ---------------- */
  const handleDownloadTemplate = async () => {
    try {
      const res = await apiPost('/CircularSyllabus/DownloadImportTemplate', {});
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'CircularSyllabus_ImportTemplate.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showSnack('Failed to download template', 'error');
    }
  };

  const handleImport = async () => {
    if (!importFile) { showSnack('Please choose an Excel file first', 'error'); return; }
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await apiPostForm(`/CircularSyllabus/ImportFromExcel?Branch_id=${selectedBranchId}`, fd);
      const d = await safeJ(res);
      if (res.ok) {
        setImportResult(d);
        showSnack(d?.Msg || 'Import completed');
        loadList();
      } else showSnack(d?.Msg || 'Import failed', 'error');
    } catch (e) {
      console.error(e);
      showSnack('Error importing file', 'error');
    } finally { setImporting(false); }
  };

  /* ============================== render ============================== */
  return (
    <Box component="main" sx={{
      width: { md: `calc(100% - ${drawerWidth}px)` }, ml: { md: `${drawerWidth}px` },
      mt: { xs: 0, sm: '64px' }, p: { xs: 1.5, sm: 3 }, backgroundColor: COLORS.bg, minHeight: '100vh',
    }}>
      <LoadingBar progress={loadingBarProgress} onLoaderFinished={() => setLoadingBarProgress(0)} color={COLORS.primary} height={4} />

      <AppBar position="static" style={{ backgroundColor: COLORS.primary }}>
        <Toolbar>
          <SyllabusIcon sx={{ mr: 1.5 }} />
          <Box>
            <Typography variant="h5" component="div" style={{ fontWeight: 'bold' }}>Manage Syllabus</Typography>
            <Typography variant="caption">Plan monthly themes · Track coverage · Clone across years</Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* KPI strip */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 2, mb: 1 }}>
        <KpiBox label="Syllabus" value={kpis.totalSyllabus} />
        <KpiBox label="Completed" value={kpis.completed} />
        <KpiBox label="Avg Coverage" value={kpis.avgCoverage} suffix="%" />
        <KpiBox label="Pending Topics" value={kpis.pendingTopics} />
      </Stack>

      <Paper elevation={3} sx={{ p: 2, width: '100%', mt: 2 }}>
        <Tabs
          value={activeTab} onChange={(e, v) => setActiveTab(v)}
          textColor="inherit"
          TabIndicatorProps={{ style: { backgroundColor: COLORS.primary, height: 3 } }}
          sx={{ borderBottom: `1px solid ${COLORS.border}`, mb: 2, '& .Mui-selected': { color: `${COLORS.primaryDark} !important`, fontWeight: 'bold' } }}
        >
          <Tab label="Syllabus List" icon={<SyllabusIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Analytics" icon={<BarChartIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Coverage Report" icon={<TrackIcon fontSize="small" />} iconPosition="start" />
        </Tabs>

        {/* ---------------- TAB 0: Syllabus List ---------------- */}
        {activeTab === 0 && (
          <>
            <FilterRow
              classId={fClass} setClassId={setFClass}
              sectionId={fSection} setSectionId={setFSection}
              subjectId={fSubject} setSubjectId={setFSubject}
              monthId={fMonth} setMonthId={setFMonth}
              financialYearId={fFy} setFinancialYearId={setFFy}
              classes={classes} sectionOptions={filtefSections} subjectOptions={filtefSubjects}
              months={months} fys={fys}
              onReload={loadList} reloading={listLoading}
              extraButtons={
                <>
                  <Button variant="outlined" sx={{
                    color: "#1B4965",
                    borderColor: "#1B4965",
                    "&:hover": {
                      borderColor: "#102A43",
                      backgroundColor: "rgba(27, 73, 101, 0.08)",
                    },}} startIcon={<ImportIcon fontSize="small" />}
                    onClick={() => { setImportFile(null); setImportResult(null); setImportOpen(true); }}>Import</Button>
                  <Button variant="outlined" sx={{
                      color: "#1B4965",
                      borderColor: "#1B4965",
                      "&:hover": {
                        borderColor: "#102A43",
                        backgroundColor: "rgba(27, 73, 101, 0.08)",
                      },
                    }} startIcon={<CloneIcon fontSize="small" />} onClick={openClone}>Clone</Button>
                  <Button variant="contained" sx={{
                    color: "#e4eaee",
                    borderColor: "#063552",
                    "&:hover": {
                      borderColor: "#063552",
                      backgroundColor: "#063552",
                    },
                  }} startIcon={<AddIcon fontSize="small" />} onClick={openCreate}>Add Syllabus</Button>
                </>
              }
            />

            <Box sx={{ mt: 2, maxWidth: 320 }}>
              <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search theme, class, subject…" />
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800, '& .MuiTableCell-root': { borderRight: `1px solid ${COLORS.border}` } }}>
                <TableHead>
                  <TableRow>
                    <TH width={40}>#</TH>
                    <TH>Class / Section</TH>
                    <TH>Subject</TH>
                    <TH>Month</TH>
                    <TH>Theme</TH>
                    <TH align="center" width={110}>Coverage</TH>
                    <TH align="center" width={110}>Status</TH>
                    <TH width={100}>Created</TH>
                    <TH align="center" width={130}>Actions</TH>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listLoading ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                  ) : pagedList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        {list.length === 0 ? 'No syllabus found. Select filters above and click Add Syllabus to get started.' : 'No results match your filters.'}
                      </TableCell>
                    </TableRow>
                  ) : pagedList.map((rec, idx) => {
                    const total = rec.totalTopics ?? 0;
                    const covered = rec.coveredTopics ?? 0;
                    const pct = rec.coveragePercentage ?? (total ? (covered / total) * 100 : 0);
                    const st = coverageState(pct);
                    const rowNum = (page - 1) * rows + idx + 1;
                    return (
                      <TableRow hover key={rec.syllabusId}>
                        <TableCell>{rowNum}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{rec.className ?? '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{rec.sectionName || 'All Sections'}</Typography>
                        </TableCell>
                        <TableCell>{rec.subjectName ?? '—'}</TableCell>
                        <TableCell>{rec.monthName ?? '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{rec.themeNo ? `${rec.themeNo}. ` : ''}{rec.themeName ?? '—'}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {rec.themeDescription || ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="center"><CoverageBar pct={pct} total={total} covered={covered} /></TableCell>
                        <TableCell align="center"><StatusChip status={st.label} /></TableCell>
                        <TableCell><Typography variant="caption">{fmt(rec.createdOn)}</Typography></TableCell>
                        <TableCell align="center">
                          <Tooltip title="Track topic coverage">
                            <IconButton size="small" color="success" onClick={() => openTrack(rec)}><TrackIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" color="warning" onClick={() => openEdit(rec)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => { setDeleteRec(rec); setDeleteOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pager total={filteredList.length} page={page} rowsPerPage={rows} onPage={setPage} onRows={setRows} />
          </>
        )}

        {/* ---------------- TAB 1: Analytics ---------------- */}
        {activeTab === 1 && (
          <Box sx={{ mt: 1 }}>
            <FilterRow
              classId={fClass} setClassId={setFClass}
              sectionId={fSection} setSectionId={setFSection}
              subjectId={fSubject} setSubjectId={setFSubject}
              monthId={fMonth} setMonthId={setFMonth}
              financialYearId={fFy} setFinancialYearId={setFFy}
              classes={classes} sectionOptions={filtefSections} subjectOptions={filtefSubjects}
              months={months} fys={fys}
              onReload={loadAnalytics} reloading={analyticsLoading}
            />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={4}>
                <OverallCompletionCard pct={analyticsKpis.avgCoverage} classCount={analyticsData.coverageByClass.length} />
              </Grid>
              <Grid item xs={12} md={4}><CoverageByClassChart data={analyticsData.coverageByClass} /></Grid>
              <Grid item xs={12} md={4}><StatusPieChart data={analyticsData.statusDistribution} /></Grid>
            </Grid>
          </Box>
        )}

        {/* ---------------- TAB 2: Coverage Report ---------------- */}
        {activeTab === 2 && (
          <Box sx={{ mt: 1 }}>
            <FilterRow
              classId={fClass} setClassId={setFClass}
              sectionId={fSection} setSectionId={setFSection}
              subjectId={fSubject} setSubjectId={setFSubject}
              monthId={fMonth} setMonthId={setFMonth}
              financialYearId={fFy} setFinancialYearId={setFFy}
              classes={classes} sectionOptions={filtefSections} subjectOptions={filtefSubjects}
              months={months} fys={fys}
              onReload={openReport} reloading={reportLoading} reloadLabel="Load"
              extraButtons={
                <Button
                  variant="contained" sx={{
                    color: "#e4eaee",
                    borderColor: "#063552",
                    "&:hover": {
                      borderColor: "#063552",
                      backgroundColor: "#063552",
                    },
}}
                  startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon fontSize="small" />}
                  onClick={handleExport} disabled={exporting}
                >
                  {exporting ? 'Exporting…' : 'Export'}
                </Button>
              }
            />
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700, '& .MuiTableCell-root': { borderRight: `1px solid ${COLORS.border}` } }}>
                <TableHead>
                  <TableRow>
                    <TH>Class / Subject</TH>
                    <TH>Theme</TH>
                    <TH>Topic</TH>
                    <TH align="center">Status</TH>
                    <TH>Modified By</TH>
                    <TH>Modified Date</TH>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportLoading ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                  ) : reportRows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}>No report data yet — click "Load" above.</TableCell></TableRow>
                  ) : reportRows.map((r, i) => (
                    <TableRow hover key={i}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{r.className} {r.sectionName}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.subjectName}</Typography>
                      </TableCell>
                      <TableCell>{r.themeName}</TableCell>
                      <TableCell>{r.topicName}</TableCell>
                      <TableCell align="center"><StatusChip status={STATUS_MAP[r.status] ?? r.status} /></TableCell>
                      <TableCell><Typography variant="caption">{r.coveredByTeacherName ?? '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{r.coveredDate ?? '—'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* ================= Add / Edit Dialog ================= */}
      <Dialog open={formOpen} onClose={() => !formSaving && setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: COLORS.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {formMode === 'create' ? <AddIcon /> : <EditIcon />}
            <Typography sx={{ fontWeight: 'bold' }}>{formMode === 'create' ? 'Add Syllabus' : 'Edit Syllabus'}</Typography>
          </Box>
          <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setFormOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {formLoadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <>
              {formMode === 'edit' ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, p: 2, mb: 2.5, borderRadius: 1, bgcolor: '#fafafa', border: `1px solid ${COLORS.border}` }}>
                  {[
                    { label: 'Class / Section', value: `${form.className ?? ''} ${form.sectionName ? '· ' + form.sectionName : ''}` },
                    { label: 'Subject', value: form.subjectName ?? '' },
                    { label: 'Month', value: form.MonthName ?? '' },
                    { label: 'Financial Year', value: form.FinancialYear ?? '' },
                  ].map((f, i) => (
                    <Box key={i}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{f.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{f.value}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Class *</InputLabel>
                      <Select label="Class *" value={form.ClassId} onChange={e => setForm(f => ({ ...f, ClassId: e.target.value, SectionId: '' }))}>
                        {classes.map(c => <MenuItem key={cId(c)} value={cId(c)}>{cName(c)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small" disabled={!form.ClassId}>
                      <InputLabel>Section</InputLabel>
                      <Select label="Section" value={form.SectionId} onChange={e => setForm(f => ({ ...f, SectionId: e.target.value }))}>
                        <MenuItem value=""><em>All Sections</em></MenuItem>
                        {formSections.map(s => <MenuItem key={secId(s)} value={secId(s)}>{secName(s)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small" disabled={!form.ClassId}>
                      <InputLabel>Subject *</InputLabel>
                      <Select label="Subject *" value={form.SubjectId} onChange={e => setForm(f => ({ ...f, SubjectId: e.target.value }))}>
                        {formSubjects.map(s => <MenuItem key={subId(s)} value={subId(s)}>{subName(s)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Month *</InputLabel>
                      <Select label="Month *" value={form.MonthId} onChange={e => setForm(f => ({ ...f, MonthId: e.target.value }))}>
                        {months.map(m => <MenuItem key={moId(m)} value={moId(m)}>{moName(m)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Financial Year *</InputLabel>
                      <Select label="Financial Year *" value={form.FinancialYearId} onChange={e => setForm(f => ({ ...f, FinancialYearId: e.target.value }))}>
                        {fys.map(f => <MenuItem key={fyId(f)} value={fyId(f)}>{fyName(f)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              <Grid container spacing={1.5} sx={{ mb: 1 }}>
                <Grid item xs={3}>
                  <TextField label="Theme No" type="number" size="small" inputProps={{ min: 0 }} fullWidth value={form.ThemeNo} onChange={e => setForm(f => ({ ...f, ThemeNo: e.target.value }))}  />
                </Grid>
                <Grid item xs={9}>
                  <TextField label="Theme Name *" size="small" fullWidth value={form.themeName} onChange={e => setForm(f => ({ ...f, themeName: e.target.value }))} placeholder="e.g. Listening Skills" />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Theme Description" size="small" fullWidth multiline rows={2} value={form.themeDescription} onChange={e => setForm(f => ({ ...f, themeDescription: e.target.value }))} placeholder="Short description of this month's theme" />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontWeight: 'bold' }}>Topics</Typography>
                <Button size="small" color="warning" startIcon={<AddIcon fontSize="small" />} onClick={addTopicRow}>Add Topic</Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
                {form.Topics.map((t, i) => (
                  <Box key={t._id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: 1, border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold', flexShrink: 0 }}>{i + 1}</Box>
                    <TextField size="small" fullWidth value={t.TopicName} placeholder={`Topic ${i + 1} name`} onChange={e => updateTopicName(t._id, e.target.value)} />
                    <IconButton size="small" color="error" onClick={() => removeTopicRow(t._id)}><CloseIcon fontSize="small" /></IconButton>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={() => setFormOpen(false)} variant="outlined" color="secondary" sx={{ flex: 1 }}>Cancel</Button>
          <Button onClick={handleSaveForm} variant="contained" color="warning" disabled={formSaving}
            startIcon={formSaving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />} sx={{ flex: 1 }}>
            {formSaving ? 'Saving…' : formMode === 'create' ? 'Create Syllabus' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Track Topic Coverage Dialog ================= */}
      <Dialog open={trackOpen} onClose={() => !trackSaving && setTrackOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: COLORS.primaryDark, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 'bold' }}>Topic Coverage</Typography>
            <Typography variant="caption">{trackSyllabus?.themeName} · {trackSyllabus?.className} {trackSyllabus?.sectionName}</Typography>
          </Box>
          <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setTrackOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {trackLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, maxHeight: 440, overflowY: 'auto', pr: 0.5 }}>
              {trackTopics.map((t) => {
                const isExpanded = expandedRemarks[t.TrackingId] || false;
                return (
                  <Box key={t.TrackingId} sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${t._dirty ? COLORS.primary : COLORS.border}`, bgcolor: t._dirty ? '#fff7ed' : '#fff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: 1, border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold', flexShrink: 0 }}>{t.TopicSequence}</Box>
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', flex: 1 }}>{t.TopicName}</Typography>
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select value={t.Status} onChange={e => updateTrackTopic(t.TrackingId, { Status: e.target.value })}>
                          <MenuItem value="P">Pending</MenuItem>
                          <MenuItem value="IP">In Progress</MenuItem>
                          <MenuItem value="C">Covered</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <TextField size="small" fullWidth placeholder="Add new remarks (optional)" value={t.Description} onChange={e => updateTrackTopic(t.TrackingId, { Description: e.target.value })} />
                    {t.CoveredDescription && t.CoveredDescription !== t.Description && (
                      <>
                        <Button size="small" color="warning" onClick={() => setExpandedRemarks(prev => ({ ...prev, [t.TrackingId]: !prev[t.TrackingId] }))} sx={{ mt: 1, p: 0, justifyContent: 'flex-start' }}>
                          {isExpanded ? 'Hide Old Remarks' : 'Show Old Remarks'}
                        </Button>
                        {isExpanded && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: '#fafafa', borderRadius: 1, border: `1px solid ${COLORS.border}` }}>
                            <Typography variant="caption" color="text.secondary">{t.CoveredDescription}</Typography>
                          </Box>
                        )}
                      </>
                    )}
                    {t.CoveredByTeacherName && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Last updated by {t.CoveredByTeacherName}{t.CoveredDate ? ` · ${t.CoveredDate}` : ''}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={() => setTrackOpen(false)} variant="outlined" color="secondary" sx={{ flex: 1 }}>Cancel</Button>
          <Button onClick={handleSaveTrack} variant="contained" color="success" disabled={trackSaving}
            startIcon={trackSaving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />} sx={{ flex: 1 }}>
            {trackSaving ? 'Saving…' : 'Save Coverage'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Clone Dialog ================= */}
      <Dialog open={cloneOpen} onClose={() => !cloneSaving && setCloneOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: COLORS.info, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CloneIcon /><Typography sx={{ fontWeight: 'bold' }}>Clone Syllabus to New Year</Typography></Box>
          <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setCloneOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Grid container spacing={1.5} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>From Financial Year *</InputLabel>
                <Select label="From Financial Year *" value={cloneForm.FromFinancialYearId} onChange={e => setCloneForm(f => ({ ...f, FromFinancialYearId: e.target.value }))}>
                  {fys.map(f => <MenuItem key={fyId(f)} value={fyId(f)}>{fyName(f)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
  <FormControl fullWidth size="small" error={cloneForm.ToFinancialYearId && cloneForm.ToFinancialYearId === cloneForm.FromFinancialYearId}>
    <InputLabel>To Financial Year *</InputLabel>
    <Select label="To Financial Year *" value={cloneForm.ToFinancialYearId} onChange={e => setCloneForm(f => ({ ...f, ToFinancialYearId: e.target.value }))}>
      {fys
        .filter(f => fyId(f) !== cloneForm.FromFinancialYearId)
        .map(f => <MenuItem key={fyId(f)} value={fyId(f)}>{fyName(f)}</MenuItem>)}
    </Select>
    {cloneForm.ToFinancialYearId && cloneForm.ToFinancialYearId === cloneForm.FromFinancialYearId && (
      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
        Must differ from From Year
      </Typography>
    )}
  </FormControl>
</Grid>
          </Grid>

          {cloneForm.FromFinancialYearId && cloneClasses.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {cloneClasses.map(c => (
                <Chip
                  key={c.classId} label={`${c.className} · ${c.syllabusCount}`} size="small"
                  onClick={() => setCloneForm(f => ({ ...f, ClassId: c.classId }))}
                  color={cloneForm.ClassId === c.classId ? 'warning' : 'default'}
                  variant={cloneForm.ClassId === c.classId ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          )}

          <Grid container spacing={1.5}>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Class *</InputLabel>
                <Select label="Class *" value={cloneForm.ClassId} onChange={e => setCloneForm(f => ({ ...f, ClassId: e.target.value, SectionId: '' }))}>
                  {classes.map(c => <MenuItem key={cId(c)} value={cId(c)}>{cName(c)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small" disabled={!cloneForm.ClassId}>
                <InputLabel>Section</InputLabel>
                <Select label="Section" value={cloneForm.SectionId} onChange={e => setCloneForm(f => ({ ...f, SectionId: e.target.value }))}>
                  <MenuItem value=""><em>Section *</em></MenuItem>
                  {cloneSections.map(s => <MenuItem key={secId(s)} value={secId(s)}>{secName(s)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small" disabled={!cloneForm.ClassId}>
                <InputLabel>Subject</InputLabel>
                <Select label="Subject" value={cloneForm.SubjectId} onChange={e => setCloneForm(f => ({ ...f, SubjectId: e.target.value }))}>
                  <MenuItem value=""><em>Subject *</em></MenuItem>
                  {cloneSubjects.map(s => <MenuItem key={subId(s)} value={subId(s)}>{subName(s)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select label="Month" value={cloneForm.MonthId} onChange={e => setCloneForm(f => ({ ...f, MonthId: e.target.value }))}>
                  <MenuItem value=""><em>Month *</em></MenuItem>
                  {months.map(m => <MenuItem key={moId(m)} value={moId(m)}>{moName(m)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: COLORS.infoBg, border: '1px solid #90caf9' }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={cloneForm.CloneTopics} onChange={e => setCloneForm(f => ({ ...f, CloneTopics: e.target.checked }))} />}
              label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Copy topic tracking rows</Typography>}
            />
            <br />
            <FormControlLabel
              control={<Checkbox size="small" checked={cloneForm.ResetTracking} onChange={e => setCloneForm(f => ({ ...f, ResetTracking: e.target.checked }))} />}
              label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Reset all statuses to Pending</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={() => setCloneOpen(false)} variant="outlined" color="secondary" sx={{ flex: 1 }}>Cancel</Button>
          <Button onClick={handleClone} variant="contained" color="info" disabled={cloneSaving}
            startIcon={cloneSaving ? <CircularProgress size={14} color="inherit" /> : <SwapIcon />} sx={{ flex: 1 }}>
            {cloneSaving ? 'Cloning…' : 'Clone Syllabus'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Import Dialog ================= */}
      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: COLORS.success, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ImportIcon /><Typography sx={{ fontWeight: 'bold' }}>Bulk Import</Typography></Box>
          <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setImportOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
            <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 1, bgcolor: COLORS.infoBg, border: '1px solid #90caf9' }}>
  <Typography sx={{ fontWeight: 'bold', fontSize: '0.8rem', mb: 0.8 }}>
    How to use this template:
  </Typography>
  <Box component="ol" sx={{ m: 0, pl: 2.2, '& li': { fontSize: '0.75rem', mb: 0.4 } }}>
    <li>Use exact Class Name as shown in your school system (e.g., 'Nursery', 'Class 1', 'KG')</li>
    <li>Section Name is optional — leave blank if not applicable</li>
    <li>Use exact Subject Name (e.g., 'English', 'Hindi', 'Maths', 'EVS')</li>
    <li>Use full Month Name (e.g., 'January', 'February')</li>
    <li>Financial Year format: '2025-2026'</li>
    <li>Theme No: 1, 2, 3, etc. (numeric)</li>
    <li>Topic Sequence: 1, 2, 3, etc. (numeric)</li>
    <li>Each row = one topic. Multiple rows with same Theme = multiple topics under one theme</li>
    <li>Do NOT delete the header row</li>
    <li>Save file before uploading</li>
  </Box>
</Box>
          <Button fullWidth variant="outlined" color="success" startIcon={<DownloadIcon fontSize="small" />} onClick={handleDownloadTemplate} sx={{ mb: 2.5 }}>
            Download Import Template
          </Button>
          <Box component="label" sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
            border: `2px dashed ${importFile ? COLORS.success : COLORS.border}`, borderRadius: 1, p: 3, cursor: 'pointer',
            bgcolor: importFile ? COLORS.successBg : '#fafafa',
          }}>
            <CloudUploadIcon sx={{ fontSize: 34, color: importFile ? COLORS.success : COLORS.muted }} />
            <Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>{importFile ? importFile.name : 'Click to choose an Excel file'}</Typography>
            <Typography variant="caption" color="text.secondary">.xlsx format only</Typography>
            <input type="file" accept=".xlsx" hidden onChange={e => setImportFile(e.target.files?.[0] ?? null)} />
          </Box>
          {importResult && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: COLORS.successBg, border: '1px solid #a5d6a7', display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ color: COLORS.success }} fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: COLORS.success }}>
                Imported {importResult.TotalSyllabus ?? importResult.totalSyllabus ?? 0} syllabus, {importResult.TotalTopics ?? importResult.totalTopics ?? 0} topics
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={() => setImportOpen(false)} variant="outlined" color="secondary" sx={{ flex: 1 }}>Close</Button>
          <Button onClick={handleImport} variant="contained" color="success" disabled={importing || !importFile}
            startIcon={importing ? <CircularProgress size={14} color="inherit" /> : <ImportIcon />} sx={{ flex: 1 }}>
            {importing ? 'Uploading…' : 'Upload & Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Delete Confirmation Dialog ================= */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#c62828', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarnIcon fontSize="small" /> Delete Syllabus
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Typography sx={{ mb: 1 }}>
            Delete the syllabus <b>{deleteRec?.themeName}</b> for <b>{deleteRec?.className} · {deleteRec?.subjectName}</b>?
          </Typography>
          <Typography variant="caption" color="text.secondary">Only syllabus with all pending topics can be deleted.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={() => setDeleteOpen(false)} variant="outlined" color="secondary">Go Back</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}>
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4500} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.sev}>{snack.msg}</Alert>

      </Snackbar>
        <Dialog
  open={cloneConfirmOpen}
  onClose={() => !cloneSaving && setCloneConfirmOpen(false)}
  maxWidth="xs"
  fullWidth
   slotProps={{
    backdrop: {
      sx: {
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        backgroundColor: "rgba(0, 0, 0, 0.35)",
      },
    },
  }}
>
  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <WarnIcon fontSize="small" color="warning" /> Confirm Clone
  </DialogTitle>
  <DialogContent>
    <Typography sx={{ mb: 1 }}>
      Clone syllabus from{' '}
      <b>{fys.find(f => fyId(f) === cloneForm.FromFinancialYearId) ? fyName(fys.find(f => fyId(f) === cloneForm.FromFinancialYearId)) : ''}</b>{' '}
      to{' '}
      <b>{fys.find(f => fyId(f) === cloneForm.ToFinancialYearId) ? fyName(fys.find(f => fyId(f) === cloneForm.ToFinancialYearId)) : ''}</b>
      {' '}for{' '}
      <b>{classes.find(c => cId(c) === cloneForm.ClassId) ? cName(classes.find(c => cId(c) === cloneForm.ClassId)) : ''}</b>?
    </Typography>
    {cloneForm.ResetTracking && (
      <Typography variant="caption" color="error">
        This will reset all cloned topic statuses to Pending.
      </Typography>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setCloneConfirmOpen(false)} variant="outlined" disabled={cloneSaving}>
      Cancel
    </Button>
    <Button
      onClick={performClone}
      variant="contained"
      color="warning"
      disabled={cloneSaving}
      startIcon={cloneSaving ? <CircularProgress size={14} color="inherit" /> : <SwapIcon />}
    >
      {cloneSaving ? 'Cloning…' : 'Yes, Clone'}
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
}